import {
  ICombinedPriceConfig,
  IDifferentialPriceConfig,
  IDifferentialPriceForQuantityConfig,
  IDifferentialRangePriceConfig,
  IPriceConfig,
  IRangePriceConfig,
  IUnitPriceConfig,
  PriceDisplayMode,
} from "@vp/ep-pricing";

import { SavingsPercentageProps } from "../types/priceVariants/savings-percentage";
import { PriceProperties, Prices } from "../types/pricing/price-display.types";
import { isEmptyPrice } from "./price-calculator-helper";

export class PriceConfigBuilder {
  private prices: Prices;
  private priceProperties: PriceProperties;
  private locale: string;

  constructor(prices: Prices, priceProperties: PriceProperties, locale: string) {
    this.prices = prices;
    this.priceProperties = priceProperties;
    this.locale = locale;
  }

  private get baseConfig() {
    return {
      minimumFractionDigits: this.priceProperties.minimumFractionDigits,
      locale: this.locale,
      currency: this.priceProperties.currency,
      vatInc: this.priceProperties.vatInc,
    };
  }

  getCombinedConfig(priceDisplayMode: PriceDisplayMode): ICombinedPriceConfig | undefined {
    const { prices, priceProperties } = this;
    if (prices.totalListPrice && prices.totalDiscountedPrice) {
      const config: ICombinedPriceConfig = {
        ...this.baseConfig,
        listPrice: prices.totalListPrice,
        discountedListPrice: prices.totalDiscountedPrice,
        unitListPrice: prices.unitListPrice,
        unitDiscountedPrice: prices.unitDiscountedPrice,
        quantity: priceProperties.quantity,
        unitPriceForMoq: prices.unitPriceForMoq,
        priceDisplayMode,
        basePrice: prices.baseListPrice,
        discountedBasePrice: prices.baseDiscountedPrice,
        volumeDiscount: prices?.volumeDiscount,
      };

      if (!priceProperties.hideSetupFee) {
        return {
          ...config,
          setupFee: isEmptyPrice(prices.setupFeeListPrice) ? undefined : prices.setupFeeListPrice,
          discountedSetupFee: isEmptyPrice(prices.setupFeeDiscountedPrice) ? undefined : prices.setupFeeDiscountedPrice,
          showPriceSummary: true,
          showSetupFeeAndVat: true,
        };
      }

      return config;
    }
    return undefined;
  }

  getUnitPriceConfig(): IUnitPriceConfig | undefined {
    const { prices } = this;
    if (prices.unitDiscountedPrice && prices.unitListPrice) {
      const config: IUnitPriceConfig = {
        ...this.baseConfig,
        unitListPrice: prices.unitListPrice,
        unitDiscountedPrice: prices.unitDiscountedPrice,
      };

      return config;
    }
    return undefined;
  }

  getPriceOnlyConfig(): IPriceConfig | undefined {
    const { prices } = this;
    if (prices.totalDiscountedPrice && prices.totalListPrice) {
      const config: IPriceConfig = {
        ...this.baseConfig,
        listPrice: prices.totalListPrice,
        discountedListPrice: prices.totalDiscountedPrice,
      };
      return config;
    }
    return undefined;
  }

  getDifferentialRangePriceConfig(): IDifferentialRangePriceConfig | undefined {
    const { prices } = this;
    if (prices.priceDifferenceAtMinimumPrice && prices.priceDifferenceAtMaximumPrice) {
      return {
        ...this.baseConfig,
        priceDifferenceAtMinimumPrice: prices.priceDifferenceAtMinimumPrice!,
        discountedPriceDifferenceAtMinimumPrice: prices.discountedPriceDifferenceAtMinimumPrice ?? prices.priceDifferenceAtMinimumPrice,
        priceDifferenceAtMaximumPrice: prices.priceDifferenceAtMaximumPrice!,
        discountedPriceDifferenceAtMaximumPrice: prices.discountedPriceDifferenceAtMaximumPrice ?? prices.priceDifferenceAtMaximumPrice,
      };
    }
    return undefined;
  }

  getDifferentialConfig(priceDisplayMode: PriceDisplayMode): IDifferentialPriceConfig | undefined {
    const { prices, priceProperties } = this;

    if (prices.differentialListPrice && prices.differentialDiscountedPrice) {
      return {
        ...this.baseConfig,
        priceDifference: prices.differentialListPrice,
        priceDifferenceDiscounted: prices.differentialDiscountedPrice ?? prices.differentialListPrice,
        unitPriceDifference: prices.differentialUnitListPrice,
        unitPriceDifferenceDiscounted: prices.differentialUnitDiscountedPrice ?? prices.differentialUnitListPrice,
        showComparativeDisplay: priceProperties.showComparativeDisplay,
        priceDisplayMode,
      };
    }
    return undefined;
  }

  getDifferentialForQuantityConfig(): IDifferentialPriceForQuantityConfig | undefined {
    const { prices, priceProperties } = this;

    if (prices.differentialListPrice && prices.differentialDiscountedPrice && priceProperties.quantity) {
      return {
        ...this.baseConfig,
        priceDifference: prices.differentialListPrice,
        priceDifferenceDiscounted: prices.differentialDiscountedPrice,
        quantity: priceProperties.quantity,
      };
    }
    return undefined;
  }

  getSavingsOnlyConfig(): SavingsPercentageProps {
    return {
      savingsPercentage: this.prices.savingsPercentage,
      quantity: this.priceProperties.quantity,
      moq: this.priceProperties.moq,
      vatInc: this.priceProperties.vatInc,
    };
  }

  getRangePriceConfig(isTile: boolean = false): IRangePriceConfig | undefined {
    const { prices, priceProperties } = this;
    const showSetUpFeeInfo = !priceProperties.hideSetupFee;

    if (prices.lowestListPrice && prices.highestListPrice) {
      return {
        ...this.baseConfig,
        lowestListPrice: prices.lowestListPrice,
        highestListPrice: prices.highestListPrice,
        lowestDiscountedPrice: prices.lowestDiscountedPrice,
        highestDiscountedPrice: prices.highestDiscountedPrice,
        setupFee: showSetUpFeeInfo && !isEmptyPrice(prices.setupFeeListPrice) ? prices.setupFeeListPrice : undefined,
        discountedSetupFee: showSetUpFeeInfo && !isEmptyPrice(prices.setupFeeDiscountedPrice) ? prices.setupFeeDiscountedPrice : undefined,
        isTile,
        showSetupFeeInfo: showSetUpFeeInfo,
      };
    }
    return undefined;
  }
}
